import { lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import { LoaderSymbol } from "../../components/loading";
import "./SdmsPackageDetail.css"; // the house `.dv-x` corner-close lives here

// A QC Batch detail opened as a MODAL POPUP over whatever page launched it. The
// data-package detail's "QC Batches" button and the SDMS Result Validation /
// Approval list's QCBatchID link both use this ONE component, so the behaviour and
// styling stay identical (standing rule 8 — never a second implementation). The
// popup keeps the launching page mounted behind it instead of a full page switch
// (user requests 2026-10-30). Read-only (viewOnly), seeded by the batch's
// analytical-batch Oid; QcBatchDetailPage's detail fetch (keyed on existingAbOid)
// resolves test/method/matrix/template from it.
//
// QcBatchDetailPage statically imports the SDMS entry screen, so it is lazy-loaded
// here to avoid a static import cycle, exactly as SdmsResultEntryPage loads it.
const QcBatchDetailPage = lazy(() => import("./QcBatchDetailPage"));

export interface QcBatchModalProps {
  /** The batch's analytical-batch Oid (QcBatchSeed.existingAbOid). */
  batchOid: string;
  /** Display batch id for the header caption (optional). */
  batchId?: string | null;
  onClose: () => void;
}

/** QC Batch detail in a centered modal popup over the launching page. */
export function QcBatchModal({ batchOid, batchId, onClose }: QcBatchModalProps) {
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      style={{ position: "fixed", inset: 0, zIndex: 1300, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ position: "relative", background: "var(--ai-card, #fff)", width: "min(1280px, 96vw)", height: "min(90vh, 920px)", borderRadius: 10, boxShadow: "0 20px 60px rgba(0,0,0,0.35)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Corner close (×) — icon only, the house `.dv-x` pattern (user request
            2026-10-30: "close icon is enough, no need close text"). Anchors to the
            card's top-right because the card is position:relative. */}
        <button className="dv-x" title="Close (keeps the page behind it)" aria-label="Close" onClick={onClose}>
          <i className="ti ti-x" />
        </button>
        <div style={{ display: "flex", alignItems: "center", padding: "10px 48px 10px 16px", borderBottom: "1px solid var(--ai-border, #e5e7eb)", fontWeight: 600, flex: "0 0 auto" }}>
          <span><i className="ti ti-chart-dots" style={{ marginRight: 6 }} />QC Batch{batchId ? ` — ${batchId}` : ""}</span>
        </div>
        <div style={{ flex: "1 1 auto", overflow: "auto", position: "relative" }}>
          <Suspense fallback={<div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: 40 }}><LoaderSymbol /> Opening QC Batch…</div>}>
            <QcBatchDetailPage
              embedded
              seed={{ testMethodId: null, test: null, method: null, matrix: null, templateID: null, existingAbOid: batchOid, viewOnly: true }}
              onBack={onClose}
            />
          </Suspense>
        </div>
      </div>
    </div>,
    document.body,
  );
}
